// API client. Centralized fetch wrapper with auth token injection, error handling, and base URL config.
import { supabase } from '@/config/supabase';

if (!import.meta.env.VITE_API_URL && import.meta.env.PROD) {
  console.error(
    '[apiClient] VITE_API_URL is not set. All API calls will target http://localhost:3001/api ' +
    'which will fail in production. Set VITE_API_URL in your environment.'
  );
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

function hasElectronAuth(): boolean {
  return typeof window !== 'undefined'
    && !!window.electronAPI
    && typeof window.electronAPI.getAuthToken === 'function';
}

export async function getElectronAuthToken(): Promise<string | null> {
  if (!hasElectronAuth()) return null;
  const token = await window.electronAPI!.getAuthToken();
  return token || null;
}

/**
 * Get the current auth token from Electron IPC / sessionStorage / Supabase
 */
export async function getAuthToken(): Promise<string | null> {
  const electronToken = await getElectronAuthToken();
  if (electronToken) return electronToken;

  // H8: Prefer a fresh Supabase token over a potentially stale sessionStorage value
  if (supabase) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        sessionStorage.setItem('authToken', session.access_token);
        return session.access_token;
      }
    } catch { /* fall through to sessionStorage */ }
  }

  const sessionToken = sessionStorage.getItem('authToken');
  if (sessionToken) return sessionToken;

  const mockUserRole = localStorage.getItem('userRole');
  if (mockUserRole && import.meta.env.DEV) {
    return 'mock-dev-token';
  }

  return null;
}

/**
 * Create headers with authentication token
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

/**
 * Refresh the auth token using either Supabase's SDK or our backend /auth/refresh
 * endpoint (whichever is available). Single-flight — concurrent 401s share one
 * refresh request. Returns the new access token, or null if refresh failed.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAuthToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    // Preferred path: Supabase SDK (also rotates refresh token server-side)
    if (supabase) {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session?.access_token) {
          sessionStorage.setItem('authToken', data.session.access_token);
          if (data.session.refresh_token) {
            sessionStorage.setItem('refreshToken', data.session.refresh_token);
          }
          return data.session.access_token;
        }
      } catch {
        // Fall through to backend refresh
      }
    }

    // Fallback: backend /auth/refresh — works even when Supabase client isn't
    // initialized (e.g., Electron context or missing env vars).
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        // Refresh token itself is invalid — session is truly dead
        if (res.status === 401 || res.status === 400) {
          clearAuthState();
        }
        return null;
      }

      const data = await res.json() as { token?: string; refreshToken?: string };
      if (!data.token) return null;

      sessionStorage.setItem('authToken', data.token);
      if (data.refreshToken) sessionStorage.setItem('refreshToken', data.refreshToken);
      return data.token;
    } catch {
      return null;
    }
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Authenticated fetch wrapper. On 401, attempts a single token refresh and
 * retries the request with the fresh token.
 */
export async function authFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (response.status !== 401) return response;

  const newToken = await refreshAuthToken();
  if (!newToken) return response;

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${newToken}`,
    },
  });
}

/** Extract a readable error message from a parsed error response body. */
function extractErrorMessage(body: Record<string, unknown>, status: number): string {
  // Global error handler format: { error: { code, message } }
  if (body?.error && typeof body.error === 'object') {
    return (body.error as Record<string, unknown>).message as string ?? `HTTP ${status}`;
  }
  // Flat format: { error: "string" } or { message: "string" }
  if (typeof body?.error === 'string') return body.error;
  if (typeof body?.message === 'string') return body.message;
  return `HTTP ${status}`;
}

/**
 * GET request with authentication
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  const response = await authFetch(endpoint);

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(body, response.status));
  }

  return response.json();
}

/**
 * POST request with authentication
 */
export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await authFetch(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(body, response.status));
  }

  return response.json();
}

/**
 * PATCH request with authentication
 */
export async function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  const response = await authFetch(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(body, response.status));
  }

  return response.json();
}

/**
 * DELETE request with authentication
 */
export async function apiDelete<T>(endpoint: string): Promise<T> {
  const response = await authFetch(endpoint, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(body, response.status));
  }

  return response.json();
}

/**
 * Clear all auth state from storage (for logout or session invalidation)
 */
export function clearAuthState(): void {
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('refreshToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('user');
  localStorage.removeItem('authUid');
  localStorage.removeItem('userId');
  localStorage.removeItem('schoolId');
}

/**
 * C3/H11: Validate the current session server-side.
 * Returns the server-verified role, or null if the session is invalid.
 */
export async function validateSession(): Promise<string | null> {
  try {
    const token = await getAuthToken();
    if (!token) return null;

    const email = localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user')!).email
      : '';
    const authUid = localStorage.getItem('authUid') || '';

    // If we don't have stored credentials, fall back to the local role
    if (!email || !authUid) {
      return localStorage.getItem('userRole');
    }

    const res = await fetch(`${API_URL}/auth/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ email, authUid }),
    });
    if (res.status === 401) return null;
    if (!res.ok) {
      // Server is reachable but returned an error — session might still be valid,
      // avoid logging out on transient errors
      return localStorage.getItem('userRole');
    }
    const data = await res.json();
    return data.user?.role || null;
  } catch {
    // Network error — don't invalidate on connectivity issues
    return localStorage.getItem('userRole');
  }
}

export { API_URL };
